import React from 'react';

const PHASE_COLORS = {
  'Análisis': '#534AB7',
  'Diseño': '#1D9E75',
  'Programación': '#378ADD',
  'Pruebas unitarias': '#D85A30',
  'Pruebas UAT': '#D4537E',
  'Documentación': '#639922',
  'Despliegue': '#BA7517',
};

export default function ResultView({ result, onSave, onBack, error }) {
  const phases = result.phases || {};
  const phaseEntries = Object.entries(phases);
  const maxHours = Math.max(...phaseEntries.map(([, p]) => p.hours || 0), 1);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-gray-400 hover:text-gray-600 text-sm mb-1">← Volver</button>
          <h1 className="text-2xl font-bold text-gray-800">{result.projectName || 'Resultado'}</h1>
        </div>
        <button
          onClick={onSave}
          className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          💾 Guardar estimación
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {/* Summary card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Complejidad</p>
            <p className="text-xl font-bold mt-1">{result.complexity}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Horas totales</p>
            <p className="text-xl font-bold text-blue-600 mt-1">{result.total_hours}h</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Confianza</p>
            <p className="text-xl font-bold mt-1">{result.confidence}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Fases</p>
            <p className="text-xl font-bold mt-1">{phaseEntries.length}</p>
          </div>
        </div>
        {result.project_summary && (
          <p className="mt-4 text-sm text-gray-600 border-t border-gray-100 pt-4">{result.project_summary}</p>
        )}
      </div>

      {/* Phases chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Distribución por fase SDLC</h2>
        <div className="space-y-3">
          {phaseEntries.map(([name, data]) => (
            <div key={name} className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-36 truncate">{name}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div
                  className="h-full rounded-full flex items-center px-2"
                  style={{
                    width: `${(data.hours / maxHours) * 100}%`,
                    backgroundColor: PHASE_COLORS[name] || '#888',
                    minWidth: '40px',
                  }}
                >
                  <span className="text-xs text-white font-medium">{data.hours}h</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline */}
      {result.timeline && result.timeline.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Timeline estimado</h2>
          <div className="space-y-2">
            {result.timeline.map((phase, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: phase.color || '#888' }}
                ></div>
                <span className="w-32 text-gray-700">{phase.phase}</span>
                <span className="text-gray-500">{phase.start} → {phase.end}</span>
                <span className="text-gray-400">({phase.days} días)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risks & Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {result.risks && result.risks.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-800 mb-3">⚠️ Riesgos</h3>
            <ul className="space-y-1">
              {result.risks.map((risk, idx) => (
                <li key={idx} className="text-sm text-gray-600 flex gap-2">
                  <span className="text-orange-500">•</span> {risk}
                </li>
              ))}
            </ul>
          </div>
        )}
        {result.recommendations && result.recommendations.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-800 mb-3">💡 Recomendaciones</h3>
            <ul className="space-y-1">
              {result.recommendations.map((rec, idx) => (
                <li key={idx} className="text-sm text-gray-600 flex gap-2">
                  <span className="text-green-500">•</span> {rec}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Technologies */}
      {result.technologies && result.technologies.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-3">🛠 Tecnologías</h3>
          <div className="flex flex-wrap gap-2">
            {result.technologies.map((tech, idx) => (
              <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                {tech}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
